/**
 * MAIN_VIEWS registry — full-canvas views that take over the central column
 * when `openMain(viewId)` is called (see UI_TAXONOMY.md §4-6).
 *
 * Each view receives its props (passed to `openMain`) spread onto it, plus an
 * `onClose` callback it can use to return to chat mode. The MainSlot wrapper
 * owns the top-bar chrome (back button, title, expand/collapse toggle) so
 * views don't reimplement it themselves.
 *
 * To add a new main view:
 *   1. Build a component that accepts whatever props it needs + `onClose`.
 *   2. Add an entry below.
 *   3. Call `openMain('<id>', {...props})` from anywhere that has access to
 *      `useLayoutStore`.
 */

import type { ComponentType } from 'react';
import { CanvasView } from "@/modules/chat/components/views/CanvasView";
import { InfographicView } from "@/modules/chat/components/views/InfographicView";
import DocxEditorViewEntry from "@/modules/chat/components/views/DocxEditorViewEntry";
import XlsxViewerViewEntry from "@/modules/chat/components/views/XlsxViewerViewEntry";
import PdfViewerViewEntry from "@/modules/chat/components/views/PdfViewerViewEntry";
import { MailComposeView } from "@/modules/chat/components/views/MailComposeView";

export interface MainViewEntry {
  /**
   * The view component. Loose typing here so each registered view can
   * declare its own prop shape — the registry doesn't try to be type-safe
   * across heterogeneous views. Call sites pass the right props.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: ComponentType<any>;
  /**
   * If true, opens expanded (full-width, hides chat) instead of split
   * (narrow chat visible alongside). User can always toggle manually.
   * See UI_TAXONOMY.md §4 rules for main slot.
   */
  defaultExpanded?: boolean;
  /** Shown in the MainSlot top-bar chrome. */
  title?: string;
}

export const MAIN_VIEWS: Record<string, MainViewEntry> = {
  canvas: {
    component: CanvasView,
    // Split by default — chat stays visible so the user can keep talking to
    // the agent while the canvas is open. User can expand to full width via
    // the MainSlot chrome toggle.
    defaultExpanded: false,
    title: 'Холст',
  },
  // Infographics live in `state.infographic`, written by the
  // `render_infographic` tool (bound to the `infographics` skill). Split by
  // default — user can keep chatting while the infographic is visible.
  // Single-slot: each call overwrites the viewer; history of past
  // infographics stays in the chat as plates, clicking one pins its spec.
  infographic: {
    component: InfographicView,
    defaultExpanded: false,
    title: 'Инфографика',
  },
  // Opened via `openMain('docxEditor', { path })` from FilesPanel when the
  // user clicks a `.docx` row. Entry component is a thin wrapper that
  // lazy-loads the real view so the ~MB docx-js-editor bundle stays out
  // of the initial page load.
  //
  // Split by default — keeps the chat visible next to the document so
  // users can talk to the agent about what they're reading. The page
  // still fits because DocxEditorView auto-fits its zoom to container
  // width (`PAGE_WIDTH_PX` heuristic), and the editor's own toolbar
  // scrolls horizontally within its rounded container. Full-width is
  // always one click away via the MainSlot chrome toggle.
  docxEditor: {
    component: DocxEditorViewEntry,
    defaultExpanded: false,
    title: 'Документ',
  },
  // Opened via `openMain('xlsxViewer', { path })` from FilesPanel when the
  // user clicks an `.xlsx`/`.csv` row. Like docxEditor, the entry component
  // lazy-loads the real view so the ~150KB SheetJS parser stays out of the
  // initial page load. Read-only preview (no save loop) — split by default
  // so the user can keep chatting about the data.
  xlsxViewer: {
    component: XlsxViewerViewEntry,
    defaultExpanded: false,
    title: 'Таблица',
  },
  // Opened via `openMain('pptxViewer'|'pdfViewer', { path })` from FilesPanel.
  // Both render the same PdfViewerView (lazy via its Entry) — the backend
  // converts presentations to PDF via LibreOffice (backend/officeconv.py) and
  // passes real PDFs through; pdf.js paints one page at a time onto a canvas
  // with a slim ‹ N / total › control (no native PDF toolbar). No client-side
  // office renderer ships. Two ids only differ by the top-bar title. Split by
  // default so the user can keep chatting alongside the preview.
  pptxViewer: {
    component: PdfViewerViewEntry,
    defaultExpanded: false,
    title: 'Презентация',
  },
  pdfViewer: {
    component: PdfViewerViewEntry,
    defaultExpanded: false,
    title: 'PDF',
  },
  // Opened automatically by ChatPage when a `compose_email` interactive
  // tool call enters running state with streamed args. Split by default —
  // the user can keep chatting while editing the draft.
  'mail-compose': {
    component: MailComposeView,
    defaultExpanded: false,
    title: 'Новое письмо',
  },
};
